import React, { useState, useEffect, useMemo } from 'react';
import { User, Order, DesignerServiceRate, Transaction, ServiceCategory, OrderStatus } from '../types';
import { Button, Card, Input, StatusBadge } from './ui';
import { ArrowLeft, DollarSign, Calendar, FileText, Download, Save, TrendingUp, CheckCircle, Clock } from 'lucide-react';
import { generatePDF } from '../utils/pdfGenerator';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

interface DesignerDetailViewProps {
    designer: User;
    orders: Order[];
    rates: DesignerServiceRate[];
    transactions: Transaction[];
    services: ServiceCategory[];
    onClose: () => void;
    onRateUpdate: (designerId: string, serviceId: string, rate: number) => Promise<void>;
}

export const DesignerDetailView: React.FC<DesignerDetailViewProps> = ({
    designer,
    orders,
    rates,
    transactions,
    services,
    onClose,
    onRateUpdate
}) => {
    const [activeTab, setActiveTab] = useState<'overview' | 'cases' | 'rates' | 'financials' | 'invoice'>('overview');

    // -- Invoice State --
    const [invoiceDateRange, setInvoiceDateRange] = useState({
        start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
    });
    const [invoiceItems, setInvoiceItems] = useState<any[]>([]);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    // -- Derived Data --
    const designerOrders = useMemo(() => orders.filter(o => o.technicianId === designer.id), [orders, designer.id]);

    const completedOrders = useMemo(() => designerOrders.filter(o => o.status === OrderStatus.DELIVERED), [designerOrders]);

    const totalCasesFinished = completedOrders.length;
    const activeCases = designerOrders.filter(o => o.status !== OrderStatus.DELIVERED && o.status !== OrderStatus.CANCELLED).length;

    // -- Helpers --
    const getRate = (serviceId: string) => rates.find(r => r.designerId === designer.id && r.serviceId === serviceId)?.rate || 0;

    const calculateEarnings = (orderItems: any[]) => {
        let total = 0;
        orderItems.forEach(item => {
            total += getRate(item.serviceId) * item.teeth.length;
        });
        return total;
    };

    const handleGenerateInvoicePreview = () => {
        const start = new Date(invoiceDateRange.start);
        const end = new Date(invoiceDateRange.end);
        // Include end date fully
        end.setHours(23, 59, 59, 999);

        const items = completedOrders.filter(o => {
            const d = new Date(o.createdAt); // Or deliveredAt if available, using createdAt for now as proxy or should we use delivery date?
            // Usually invoices are based on delivery date, but let's stick to simple date filter on order.
            return d >= start && d <= end;
        }).map(o => {
            const orderTotal = calculateEarnings(o.items);
            return {
                orderId: o.id,
                patient: o.patientName,
                doctor: o.doctorName,
                date: o.createdAt,
                itemsCount: o.items.reduce((acc: number, i: any) => acc + i.teeth.length, 0),
                total: orderTotal
            };
        });
        setInvoiceItems(items);
    };

    const handleDownloadInvoice = async () => {
        setIsGeneratingPdf(true);
        console.log('Starting invoice generation...');
        // Wait for render
        setTimeout(async () => {
            try {
                const element = document.getElementById('invoice-preview');
                if (!element) {
                    throw new Error('Invoice preview element not found');
                }
                await generatePDF('invoice-preview', `Invoice_${designer.name}_${invoiceDateRange.start}_${invoiceDateRange.end}`);
                console.log('Invoice generated successfully');
            } catch (error) {
                console.error('Invoice generation failed:', error);
                alert('Failed to generate invoice. Please try again. Check console for details.');
            } finally {
                setIsGeneratingPdf(false);
            }
        }, 500);
    };

    // -- Renderers --

    const renderOverview = () => (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in">
            <Card className="p-6 bg-zinc-900 border-zinc-800">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500">
                        <ActivityIcon />
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-white">{activeCases}</h3>
                        <p className="text-zinc-500 text-sm">Active Cases</p>
                    </div>
                </div>
            </Card>
            <Card className="p-6 bg-zinc-900 border-zinc-800">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center text-green-500">
                        <CheckCircle size={24} />
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-white">{totalCasesFinished}</h3>
                        <p className="text-zinc-500 text-sm">Total Completed</p>
                    </div>
                </div>
            </Card>
            <Card className="p-6 bg-zinc-900 border-zinc-800">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center text-purple-500">
                        <TrendingUp size={24} />
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-white">
                            {completedOrders.length > 0
                                ? (completedOrders.reduce((acc: number, o) => acc + o.items.length, 0) / completedOrders.length).toFixed(1)
                                : 0}
                        </h3>
                        <p className="text-zinc-500 text-sm">Avg Items/Case</p>
                    </div>
                </div>
            </Card>

            {/* Recent Activity */}
            <div className="md:col-span-3">
                <h3 className="text-lg font-bold text-white mb-4">Recent Completed Cases</h3>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-zinc-950 text-zinc-500 text-xs font-bold uppercase">
                            <tr>
                                <th className="p-4">Date</th>
                                <th className="p-4">Patient</th>
                                <th className="p-4">Items</th>
                                <th className="p-4 text-right">Earning</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800">
                            {completedOrders.slice(0, 5).map(o => (
                                <tr key={o.id} className="hover:bg-zinc-800/50">
                                    <td className="p-4 text-zinc-400">{new Date(o.createdAt).toLocaleDateString()}</td>
                                    <td className="p-4 text-white">{o.patientName}</td>
                                    <td className="p-4 text-zinc-300">{o.items.map(i => i.serviceName).join(', ')}</td>
                                    <td className="p-4 text-right font-mono text-green-400">{calculateEarnings(o.items)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderRates = () => (
        <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white">Personal Rate Card</h2>
                <p className="text-sm text-zinc-400">Rates specifically for {designer.name}</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                <table className="w-full text-left text-sm">
                    <thead className="bg-zinc-950 text-zinc-500 text-xs font-bold uppercase">
                        <tr>
                            <th className="p-4">Service</th>
                            <th className="p-4">Category</th>
                            <th className="p-4 text-right">Standard Rate</th>
                            <th className="p-4 text-right">Designer Rate</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                        {services.flatMap(cat => cat.services.map(s => ({ ...s, category: cat.name }))).map(service => {
                            const rate = getRate(service.id);
                            return (
                                <tr key={service.id} className="hover:bg-zinc-800/50">
                                    <td className="p-4 font-medium text-white">{service.name}</td>
                                    <td className="p-4 text-zinc-500 text-xs uppercase">{service.category}</td>
                                    <td className="p-4 text-right text-zinc-500 font-mono">{service.price}</td>
                                    <td className="p-4 text-right">
                                        <input
                                            className="bg-black/30 border border-zinc-700 rounded px-2 py-1 text-right text-green-400 font-bold w-24 outline-none focus:border-blue-500"
                                            placeholder="0.00"
                                            defaultValue={rate}
                                            onBlur={(e) => {
                                                const val = parseFloat(e.target.value);
                                                if (!isNaN(val) && val !== rate) {
                                                    onRateUpdate(designer.id, service.id, val);
                                                }
                                            }}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderInvoice = () => (
        <div className="space-y-6 animate-fade-in h-full flex flex-col">
            <div className="flex flex-col md:flex-row gap-4 items-end bg-zinc-900 border border-zinc-800 p-4 rounded-xl">
                <Input
                    label="Start Date"
                    type="date"
                    value={invoiceDateRange.start}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInvoiceDateRange(prev => ({ ...prev, start: e.target.value }))}
                />
                <Input
                    label="End Date"
                    type="date"
                    value={invoiceDateRange.end}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInvoiceDateRange(prev => ({ ...prev, end: e.target.value }))}
                />
                <Button onClick={handleGenerateInvoicePreview}>
                    <FileText size={18} /> Generate Preview
                </Button>
            </div>

            {invoiceItems.length > 0 && (
                <div className="flex-1 flex flex-col gap-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-white font-bold">Invoice Preview</h3>
                        <Button onClick={handleDownloadInvoice} disabled={isGeneratingPdf}>
                            {isGeneratingPdf ? 'Generating...' : <><Download size={18} /> Download PDF</>}
                        </Button>
                    </div>

                    <div className="flex-1 overflow-y-auto bg-black rounded-xl border border-zinc-800 p-8 shadow-2xl relative">
                        <div id="invoice-preview" className="max-w-3xl mx-auto bg-black text-white p-8 min-h-[1123px] flex flex-col justify-between">
                            <div>
                                {/* Invoice Header */}
                                <div className="flex justify-between items-start border-b border-zinc-800 pb-8 mb-8">
                                    <div className="flex items-center gap-4">
                                        <img
                                            src="https://pqcchblhugugkywsfofz.supabase.co/storage/v1/object/public/meida/logo.png"
                                            alt="Galal Dental Lab"
                                            className="h-20 w-auto object-contain"
                                            crossOrigin="anonymous"
                                        />
                                        <div>
                                            <h1 className="text-3xl font-bold tracking-tight text-white mb-2">INVOICE</h1>
                                            <div className="text-zinc-500 text-sm">
                                                <p>Date: {new Date().toLocaleDateString()}</p>
                                                <p>Period: {invoiceDateRange.start} to {invoiceDateRange.end}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <h2 className="text-xl font-bold text-blue-500">Galal Dental Lab</h2>
                                        <p className="text-zinc-400 text-sm mt-1">Provider: {designer.name}</p>
                                        <p className="text-zinc-500 text-xs">{designer.email}</p>
                                    </div>
                                </div>

                                {/* Summary Cards */}
                                <div className="grid grid-cols-3 gap-4 mb-8">
                                    <div className="bg-zinc-900/50 p-4 rounded border border-zinc-800">
                                        <div className="text-zinc-500 text-xs uppercase mb-1">Total Cases</div>
                                        <div className="text-xl font-bold">{invoiceItems.length}</div>
                                    </div>
                                    <div className="bg-zinc-900/50 p-4 rounded border border-zinc-800">
                                        <div className="text-zinc-500 text-xs uppercase mb-1">Total Items</div>
                                        <div className="text-xl font-bold">{invoiceItems.reduce((acc: number, i: any) => acc + i.itemsCount, 0)}</div>
                                    </div>
                                    <div className="bg-zinc-900/50 p-4 rounded border border-zinc-800 hover:border-green-500/30 transition-colors">
                                        <div className="text-zinc-500 text-xs uppercase mb-1">Total Amount</div>
                                        <div className="text-xl font-bold text-green-400">{invoiceItems.reduce((acc: number, i: any) => acc + i.total, 0).toLocaleString()} EGP</div>
                                    </div>
                                </div>

                                {/* Table */}
                                <table className="w-full text-left text-sm mb-8">
                                    <thead className="border-b border-zinc-800 text-zinc-500 text-xs uppercase">
                                        <tr>
                                            <th className="py-3">Date</th>
                                            <th className="py-3">Order ID</th>
                                            <th className="py-3">Patient</th>
                                            <th className="py-3 text-right">Items</th>
                                            <th className="py-3 text-right">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-zinc-900">
                                        {invoiceItems.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className="py-3 text-zinc-400">{new Date(item.date).toLocaleDateString()}</td>
                                                <td className="py-3 font-mono text-zinc-500">#{item.orderId.slice(0, 6)}</td>
                                                <td className="py-3">{item.patient}</td>
                                                <td className="py-3 text-right">{item.itemsCount}</td>
                                                <td className="py-3 text-right font-mono">{item.total}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="border-t border-zinc-700">
                                        <tr>
                                            <td colSpan={4} className="py-4 text-right font-bold text-white uppercase pr-8">Total</td>
                                            <td className="py-4 text-right font-bold font-mono text-green-400 text-lg">
                                                {invoiceItems.reduce((acc: number, i: any) => acc + i.total, 0).toLocaleString()} EGP
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            {/* Footer */}
                            <div className="border-t border-zinc-800 pt-8 text-center text-zinc-600 text-xs mt-auto">
                                <p>Generated by Galal Dental Lab Management System</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    // -- Financial Helper --
    const renderFinancials = () => {
        const designerTransactions = transactions
            .filter(t => t.profileId === designer.id)
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

        return (
            <div className="space-y-6 animate-fade-in">
                <div className="grid grid-cols-2 gap-6">
                    <Card className="p-6 bg-zinc-900 border-zinc-800">
                        <div className="text-sm text-zinc-500 uppercase mb-1">Total Balance</div>
                        <div className={`text-3xl font-bold ${designer.balance < 0 ? 'text-red-400' : 'text-green-400'}`}>
                            {designer.balance.toLocaleString()} EGP
                        </div>
                        <p className="text-xs text-zinc-600 mt-2">Current Wallet Balance</p>
                    </Card>
                    <Card className="p-6 bg-zinc-900 border-zinc-800">
                        <div className="text-sm text-zinc-500 uppercase mb-1">Total Earned</div>
                        <div className="text-3xl font-bold text-white">
                            {/* Calculate rough total earned from transactions if needed, or leave dynamic */}
                            {designerTransactions.filter(t => t.type === 'DESIGNER_PAYOUT' || t.amount > 0).reduce((acc, t) => acc + t.amount, 0).toLocaleString()} EGP
                        </div>
                        <p className="text-xs text-zinc-600 mt-2">Life-time Earnings</p>
                    </Card>
                </div>

                <h3 className="text-xl font-bold text-white">Transaction History</h3>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-zinc-950 text-zinc-500 text-xs font-bold uppercase">
                            <tr>
                                <th className="p-4">Date</th>
                                <th className="p-4">Type</th>
                                <th className="p-4">Description</th>
                                <th className="p-4 text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800">
                            {designerTransactions.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="p-8 text-center text-zinc-500">No transactions found for this designer.</td>
                                </tr>
                            ) : (
                                designerTransactions.map(t => (
                                    <tr key={t.id} className="hover:bg-zinc-800/50">
                                        <td className="p-4 text-zinc-400">{new Date(t.createdAt).toLocaleDateString()}</td>
                                        <td className="p-4 text-zinc-300">
                                            <span className="bg-white/5 border border-white/10 px-2 py-1 rounded text-xs">
                                                {t.type.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td className="p-4 text-white">{t.description}</td>
                                        <td className={`p-4 text-right font-mono font-bold ${t.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            {t.amount > 0 ? '+' : ''}{t.amount}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="h-full flex flex-col bg-zinc-950 animate-fade-in-up">
            {/* Header */}
            <div className="flex items-center gap-4 border-b border-zinc-800 p-6 bg-zinc-900/50 sticky top-0 z-20 backdrop-blur-md">
                <Button variant="secondary" size="sm" onClick={onClose}>
                    <ArrowLeft size={16} /> Back
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        {designer.name}
                        <span className="text-xs bg-blue-900/30 text-blue-400 px-2 py-0.5 rounded border border-blue-500/20">Designer</span>
                    </h1>
                    <p className="text-zinc-400 text-sm">{designer.email}</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-4 border-b border-zinc-800 overflow-x-auto">
                {['overview', 'cases', 'rates', 'financials', 'invoice'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab as any)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20'
                            : 'text-zinc-400 hover:bg-zinc-900 hover:text-white'
                            }`}
                    >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
                {activeTab === 'overview' && renderOverview()}
                {activeTab === 'rates' && renderRates()}
                {activeTab === 'invoice' && renderInvoice()}
                {activeTab === 'cases' && (
                    <div className="space-y-4">
                        <h2 className="text-xl font-bold text-white">Case History</h2>
                        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-zinc-950 text-zinc-500 text-xs font-bold uppercase">
                                    <tr>
                                        <th className="p-4">Date</th>
                                        <th className="p-4">Patient</th>
                                        <th className="p-4">Status</th>
                                        <th className="p-4 text-right">Items</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-800">
                                    {designerOrders.map(o => (
                                        <tr key={o.id} className="hover:bg-zinc-800/50">
                                            <td className="p-4 text-zinc-400">{new Date(o.createdAt).toLocaleDateString()}</td>
                                            <td className="p-4 text-white font-bold">{o.patientName}</td>
                                            <td className="p-4"><StatusBadge status={o.status} /></td>
                                            <td className="p-4 text-right text-zinc-300">{o.items.length}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                {activeTab === 'financials' && (
                    <div className="text-center text-zinc-500 py-20">
                        <DollarSign size={48} className="mx-auto mb-4 opacity-50" />
                        <p>Financial history coming soon.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const ActivityIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
    </svg>
);
